import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/components/AuthProvider";
import { Button } from "@/components/ui/button";
import { Plus, Star, ArrowRight, FolderKanban } from "lucide-react";
import CreateBoardDialog from "@/components/dialogs/CreateBoardDialog";
import CreateWorkspaceDialog from "@/components/dialogs/CreateWorkspaceDialog";
import BoardCard from "@/components/BoardCard";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "react-i18next";
import OnboardingTour from "@/components/OnboardingTour";
import WorkspaceKebab, { WorkspaceContextMenu } from "@/components/WorkspaceKebab";

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [boardOpen, setBoardOpen] = useState(false);
  const [wsOpen, setWsOpen] = useState(false);

  const { data: workspaces = [], isLoading: wsLoading } = useQuery({
    queryKey: ["workspaces"],
    queryFn: async () => (await api.get("/workspaces")).data,
  });
  const { data: recentBoards = [], isLoading: boardsLoading } = useQuery({
    queryKey: ["recent-boards"],
    queryFn: async () => (await api.get("/boards/recent", { params: { limit: 8 } })).data,
  });

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return t("dashboard.greetingMorning");
    if (h < 19) return t("dashboard.greetingAfternoon");
    return t("dashboard.greetingEvening");
  })();

  return (
    <div className="h-full overflow-y-auto p-6 md:p-10" data-testid="dashboard">
      <div className="max-w-7xl mx-auto space-y-12 animate-fade-in-up">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="text-sm text-muted-foreground">{greeting},</p>
            <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">{user?.name?.split(" ")[0] || t("dashboard.helloFallback")} 👋</h1>
            <p className="text-muted-foreground mt-2 max-w-xl">
              {t("dashboard.subtitle")}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setWsOpen(true)}
              data-testid="dashboard-create-workspace"
              className="bg-white/60 dark:bg-slate-900/40 backdrop-blur"
            >
              <Plus className="h-4 w-4 mr-2" /> {t("dashboard.newWorkspace")}
            </Button>
            <Button
              onClick={() => setBoardOpen(true)}
              data-testid="dashboard-create-board"
              className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/30"
            >
              <Plus className="h-4 w-4 mr-2" /> {t("dashboard.createBoard")}
            </Button>
          </div>
        </div>

        {/* Boards destacados */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-semibold flex items-center gap-2">
              <Star className="h-4 w-4 text-amber-400 fill-amber-400" /> {t("dashboard.starred")}
            </h2>
          </div>
          {boardsLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32 rounded-2xl" />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {recentBoards.filter(b => b.is_starred).slice(0, 4).map((b) => (
                <BoardCard key={b.board_id} board={b} />
              ))}
              {recentBoards.filter(b => b.is_starred).length === 0 && (
                <div className="glass-card rounded-2xl p-6 col-span-full text-center text-sm text-muted-foreground">
                  {t("dashboard.starredEmpty")}
                </div>
              )}
            </div>
          )}
        </section>

        {/* Boards recientes */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-semibold">{t("dashboard.recentBoards")}</h2>
          </div>
          {boardsLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32 rounded-2xl" />)}
            </div>
          ) : recentBoards.length === 0 ? (
            <div className="glass-panel rounded-3xl p-10 text-center">
              <FolderKanban className="h-10 w-10 text-blue-500 mx-auto mb-3" />
              <p className="font-display text-lg">{t("dashboard.createFirst")}</p>
              <p className="text-sm text-muted-foreground mt-1">{t("dashboard.createFirstSub")}</p>
              <Button onClick={() => setBoardOpen(true)} className="mt-5 bg-blue-600 hover:bg-blue-700 text-white" data-testid="empty-create-board">
                <Plus className="h-4 w-4 mr-2" /> {t("dashboard.createBoard")}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {recentBoards.map((b) => <BoardCard key={b.board_id} board={b} />)}
            </div>
          )}
        </section>

        {/* Workspaces */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-semibold">{t("dashboard.yourWorkspaces")}</h2>
          </div>
          {wsLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {workspaces.map((w) => (
                <div key={w.workspace_id} className="relative group">
                  <WorkspaceContextMenu workspace={w}>
                    <Link
                      to={`/workspaces/${w.workspace_id}`}
                      data-testid={`dashboard-workspace-${w.workspace_id}`}
                      className="glass-card rounded-2xl p-5 hover:-translate-y-0.5 hover:shadow-lg transition block"
                    >
                      <div className="flex items-center justify-between pr-6">
                        <div className="min-w-0">
                          <p className="font-display text-lg font-semibold tracking-tight truncate">{w.name}</p>
                          <p className="text-xs text-muted-foreground line-clamp-1">{w.description || t("dashboard.workspaceFallback")}</p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-blue-500 transition shrink-0" />
                      </div>
                    </Link>
                  </WorkspaceContextMenu>
                  <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                    <WorkspaceKebab workspace={w} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <CreateBoardDialog open={boardOpen} onOpenChange={setBoardOpen} />
      <CreateWorkspaceDialog open={wsOpen} onOpenChange={setWsOpen} />
      {!boardsLoading && (
        <OnboardingTour
          boardsCount={recentBoards.length}
          onCreateBoard={() => setBoardOpen(true)}
        />
      )}
    </div>
  );
}
